import { css, LitElement, html, CSSResult, PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import { Event, Market, getEvent } from "../../api/index.js";
import numeral from "numeral";
import { BASE_HREF, ELECTION_EMBED_SLUG_DETAILS_MAP, ELECTIONS_PAGE_HREF, EMBED_NAME, HARRIS_MARKET_SLUG, PRESIDENTIAL_EVENT_SLUG, SENATE_EVENT_SLUG, SUPPORTED_EVENTS, TRUMP_MARKET_SLUG } from "./constants.js";
import loadFonts from "../../common/loadFonts.js";
import { cssVarStyling } from "../../common/styles.js";
import { styleMap } from "lit/directives/style-map.js";

loadFonts(); /* eagerly load fonts */

@customElement(EMBED_NAME)
export class ElectionEmbed extends LitElement {
    static override get styles(): CSSResult {
        return css`
            ${cssVarStyling}

            :host * {
                box-sizing: border-box;
            }

            .container {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: calc(100vw - 4px);
                height: fit-content;
                justify-content: center;
                align-items: center;
                padding: 2px;
            }

            .card-container {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 392px;
                height: fit-content;
                padding: var(--polymarket-space-5);
                background-color: var(--polymarket-color-white);
                border-radius: var(--polymarket-radii-md);
                border: 1px solid var(--polymarket-color-gray-6);
                gap: var(--polymarket-space-5);
            }

            .top-row-container {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                height: 96px;
                padding: var(--polymarket-space-4);
                border-radius: var(--polymarket-radii-xl);
                width: 100%;
                gap: var(--polymarket-space-3);
                overflow: hidden;
                background: linear-gradient(90deg, var(--polymarket-color-election-red-1) 0%, var(--polymarket-color-election-blue-1) 100%);
                background: -webkit-linear-gradient(0deg, var(--polymarket-color-election-red-1) 0%, var(--polymarket-color-election-blue-1) 100%);
            }

            :host([theme="dark"]) .top-row-container {
                background: linear-gradient(90deg, var(--polymarket-color-gray-6) 0%, var(--polymarket-color-gray-6) 100%);
                background: -webkit-linear-gradient(0deg, var(--polymarket-color-gray-6) 0%, var(--polymarket-color-gray-6) 100%);
            }

            .bottom-row-container {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                width: 100%;
                height: 32px;
                gap: var(--polymarket-space-3);
                overflow: hidden;
            }

            .top-row-left-container {
                all: unset;
                display: flex;
                flex-direction: row;
                align-items: center;
                flex: 1;
                gap: var(--polymarket-space-3);
                cursor: pointer;
            }

            .top-row-image {
                width: 60px;
                height: 60px;
                object-fit: cover;
                border-radius: var(--polymarket-radii-full);
                transition: all 100ms ease;
            }

            .top-row-right-container {
                all: unset;
                display: flex;
                flex-direction: row;
                align-items: center;
                flex: 1;
                gap: var(--polymarket-space-3);
                justify-content: flex-end;
                cursor: pointer;
            }

            .top-row-image-container {
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: var(--polymarket-space-1);
            }

            .top-row-right-container:hover .top-row-image {
                transform: scale(1.05);
            }

            .top-row-right-container:hover .market-title {
                text-decoration-color: var(--polymarket-color-black);
            }

            .top-row-left-container:hover .top-row-image {
                transform: scale(1.05);
            }

            .top-row-left-container:hover .market-title {
                text-decoration-color: var(--polymarket-color-black);
            }

            .top-row-price-change-container {
                display: flex;
                flex-direction: row;
            }

            .bottom-row-left-container {
                align-items: center;
                height: 24px;
                width: 114px;
                cursor: pointer;
                transition: opacity 100ms ease-in-out;
                opacity: 1;
                &:hover {
                    opacity: 0.8;
                }
            }

            .bottom-row-right-container {
                display: flex;
                flex-direction: row;
                align-items: center;
                width: auto;
                gap: var(--polymarket-space-3);
            }

            .typography-secondary {
                color: var(--polymarket-color-gray-3);
                font-size: var(--polymarket-font-size-sm);
                line-height: var(--polymarket-line-height-sm);
                font-weight: var(--polymarket-font-weight-medium);
                white-space: nowrap;
            }

            .button-primary {
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0px var(--polymarket-space-3);
                height: 32px;
                background-color: var(--polymarket-color-gray-6);
                border-radius: var(--polymarket-radii-md);
                color: var(--polymarket-color-black);
                font-size: var(--polymarket-font-size-md);
                line-height: var(--polymarket-line-height-md);
                font-weight: var(--polymarket-font-weight-semi);
                cursor: pointer;
                border: none;
                transition: background-color 100ms ease-in-out;
                gap: var(--polymarket-space-1);
                text-decoration: none;
                white-space: nowrap;
                &:hover {
                    background-color: var(--polymarket-color-gray-5);
                }
            }

            .market-probability {
                margin: 0;
                color: var(--polymarket-color-black);
                font-size: 28px;
                font-weight: var(--polymarket-font-weight-semi);
                white-space: nowrap;
            }

            .market-price-change {
                margin: 0;
                font-size: var(--polymarket-font-size-sm);
                font-weight: var(--polymarket-font-weight-medium);
                white-space: nowrap;
            }

            .market-title {
                margin: 0;
                color: var(--polymarket-color-black);
                font-size: var(--polymarket-font-size-md);
                line-height: var(--polymarket-line-height-md);
                font-weight: var(--polymarket-font-weight-semi);
                text-decoration: underline;
                text-decoration-color: transparent;
                text-decoration-thickness: 2px;
                transition: text-decoration-color 100ms ease;
            }

            .arrow-icon {
                transform: rotate(180deg);
                fill: var(--polymarket-color-black);
                path {
                    fill: var(--polymarket-color-black);
                }
            }

            .polymarket-logo-path {
                fill: var(--polymarket-color-gray-4);
            }

            :host([theme="dark"]) .polymarket-logo-path {
                fill: var(--polymarket-color-gray-3);
            }
        `;
    }

    @property({ type: String, attribute: "theme", reflect: true })
    private accessor theme: string = "light";

    @property({ type: String, attribute: "event" })
    private accessor eventSlug: string | undefined = undefined;

    @property({ type: String, attribute: "creator" })
    private accessor creator: string | undefined = undefined;

    @property({
        type: Boolean,
        attribute: "volume",
        converter: (value, _) => {
            if (typeof value === "string") return value === "true";
            if (typeof value === "boolean") return value;
            return false;
        },
    })
    private accessor showVolume: boolean = false;

    @state()
    private accessor _event: Event | undefined;

    @state()
    private accessor _href: string = ELECTIONS_PAGE_HREF;

    @state()
    private accessor _baseHref: string = BASE_HREF;

    @state()
    private accessor _logoSvgSize = { width: "114px", height: "24px" };

    constructor() {
        super();
        loadFonts();
    }

    override connectedCallback() {
        super.connectedCallback();
        loadFonts();
        this.fetchEventData();

        const utmParams = this.getUtmParams();
        this._baseHref = `${BASE_HREF}?${utmParams}`;
        this._href = `${ELECTIONS_PAGE_HREF}?${utmParams}`;
    }

    protected override firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.initTheme();
    }

    private initTheme() {
        document.querySelector("html")?.setAttribute("theme", this.theme);
    }

    private getUtmParams() {
        const utmMedium = "utm_medium=embed";
        const utmCampaign = "&utm_campaign=market";
        const _utmSource =
            window.location != window.parent.location && !!document.referrer
                ? document.referrer
                : `${document.location.host}${document.location.pathname}`;
        const utmSource = `&utm_source=${_utmSource}`;
        const utmContent = this.creator ? `&utm_content=${this.creator}` : "";

        return `${utmMedium}${utmCampaign}${utmSource}${utmContent}`;
    }

    private getColorForPriceChange(priceChange: number) {
        if (priceChange < 0) {
            return "var(--polymarket-color-red)"
        }

        if (priceChange > 0) {
            return "var(--polymarket-color-green)"
        }

        return "var(--polymarket-color-neutral)"
    }

    private async fetchEventData() {
        if (!this.eventSlug) {
            return;
        }

        if (!SUPPORTED_EVENTS.has(this.eventSlug)) {
            throw new Error('Unsupported event');
        }

        if (!this._event) {
            try {
                const event = await getEvent({ slug: this.eventSlug });
                this._event = event;
            } catch {
                /* swallow */
            }
        }
    }

    private getInvertedProbability(probability: number) {
        const inverse = 100 - probability;
        const roundedSum = Math.round(inverse) + Math.round(probability)

        // There's an edge case in single market events where the two probabilities appear to be greater than 100 when rounded (e.g. 23.5 + 76.5),
        // in which case we simply substract 0.5 from the inverted prob such that they appear to always equal 100 to the user
        if (roundedSum > 100) {
            return inverse - 0.5;
        }

        return inverse;
    }

    private getMarketData(market: Market, invert: boolean = false) {
        const value = market.outcomePrices[0]
            ? parseFloat(market.outcomePrices[0])
            : 0;

        const prob = Math.min(Math.max(value * 100, 0), 100);
        const priceChange = (market.oneDayPriceChange || 0) * 100;

        const probability = invert ? this.getInvertedProbability(prob) : prob;
        const oneDayPriceChange = invert ? -1 * priceChange : priceChange;

        return {
            probability,
            oneDayPriceChange,
        };
    }

    private getEventData() {
        if (!this._event) {
            return null;
        }

        const { volume } = this._event;

        switch (this.eventSlug) {
            case PRESIDENTIAL_EVENT_SLUG: {
                const trumpMarket = this._event.markets.find((m) => m.slug === TRUMP_MARKET_SLUG);
                const harrisMarket = this._event.markets.find((m) => m.slug === HARRIS_MARKET_SLUG);
                const metadata = ELECTION_EMBED_SLUG_DETAILS_MAP[PRESIDENTIAL_EVENT_SLUG];

                if (!trumpMarket || !harrisMarket) {
                    throw new Error('Expected markets');
                }

                return {
                    leftMarket: {
                        ...metadata.leftMarket,
                        ...this.getMarketData(trumpMarket),
                    },
                    rightMarket: {
                        ...metadata.rightMarket,
                        ...this.getMarketData(harrisMarket),
                    },
                    volume,
                }
            }

            case SENATE_EVENT_SLUG: {
                const [senateMarket] = this._event.markets;
                const metadata = ELECTION_EMBED_SLUG_DETAILS_MAP[SENATE_EVENT_SLUG];

                if (!senateMarket) {
                    throw new Error('Expected market')
                }

                return {
                    leftMarket: {
                        ...metadata.leftMarket,
                        ...this.getMarketData(senateMarket, true),
                    },
                    rightMarket: {
                        ...metadata.rightMarket,
                        ...this.getMarketData(senateMarket),
                    },
                    volume,
                }
            }

            default: {
                throw new Error('Unsupported event');
            }
        }
    }

    override render() {
        const eventData = this.getEventData();

        if (!eventData) {
            return html` <span>${" "}</span> `;
        }

        const { leftMarket, rightMarket, volume } = eventData;

        return html` <div class="container">
            <div
                class="card-container"
            >
                <div
                    class="top-row-container"
                >
                    <a
                        class="top-row-left-container"
                        href="${BASE_HREF}/market/${leftMarket.slug}"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <div class="top-row-image-container">
                            <img
                                class="top-row-image"
                                src="${leftMarket.image}"
                                alt="icon"
                            />

                            <p
                                class="market-title"
                            >
                                ${leftMarket.shortTitle}
                            </p>
                        </div>
                        <div class="top-row-text-container">
                            <p
                                class="market-probability"
                            >
                                ${numeral(leftMarket.probability).format("0")}%
                            </p>

                            <div class="top-row-price-change-container" style=${styleMap({ color: this.getColorForPriceChange(leftMarket.oneDayPriceChange) })}>
                                <svg style=${styleMap({ transform: leftMarket.oneDayPriceChange < 0 ? "rotate(180deg)" : undefined })} xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="16" height="16" viewBox="0 0 24 24"><path d="M5 15h14l-7-8z"/></svg>
                                <p
                                    class="market-price-change"
                                >
                                    ${numeral(leftMarket.oneDayPriceChange).format("0.0",)}%
                                </p>
                            </div>
                        </div>
                    </a>

                    <a
                        class="top-row-right-container"
                        href="${BASE_HREF}/market/${rightMarket.slug}"
                        rel="noopener noreferrer"
                        target="_blank"
                    >
                        <div class="top-row-text-container">
                            <p
                                class="market-probability"
                            >
                                ${numeral(rightMarket.probability).format("0")}%

                            <div class="top-row-price-change-container" style=${styleMap({ color: this.getColorForPriceChange(rightMarket.oneDayPriceChange), justifyContent: "flex-end" })}>
                                <svg style=${styleMap({ transform: rightMarket.oneDayPriceChange < 0 ? "rotate(180deg)" : undefined })} xmlns="http://www.w3.org/2000/svg" fill="currentColor" width="16" height="16" viewBox="0 0 24 24"><path d="M5 15h14l-7-8z"/></svg>
                                <p
                                    class="market-price-change"
                                >
                                    ${numeral(rightMarket.oneDayPriceChange).format("0.0",)}%
                                </p>
                            </div>
                        </div>

                        <div class="top-row-image-container">
                            <img
                                class="top-row-image"
                                src="${rightMarket.image}"
                                alt="icon"
                            />

                            <p
                                class="market-title"
                            >
                                ${rightMarket.shortTitle}
                            </p>
                        </div>
                    </a>
                </div>

                <div class="bottom-row-container">
                    <a
                        class="bottom-row-left-container"
                        href="${this._baseHref}"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <svg
                            width="${this._logoSvgSize.width}"
                            height="${this._logoSvgSize.height}"
                            viewBox="0 0 168 38"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M78.6391 27.3227V9.54077H75.7148V27.3227H78.6391ZM49.9319 21.095V27.3233H46.8903V10.4676H53.455C54.7469 10.4676 55.8645 10.6972 56.8079 11.1564C57.7513 11.6156 58.4697 12.2523 58.9629 13.0666C59.4561 13.8808 59.7027 14.8089 59.7027 15.8508C59.7027 16.939 59.4581 17.8767 58.9688 18.6639C58.4794 19.4511 57.767 20.0531 56.8314 20.4699C55.8958 20.8867 54.7704 21.095 53.455 21.095H49.9319ZM49.9318 18.6871H53.455C54.5432 18.6871 55.3477 18.4362 55.8683 17.9346C56.389 17.4329 56.6493 16.746 56.6493 15.8739C56.6493 15.3337 56.5358 14.8378 56.3087 14.3863C56.0817 13.9348 55.7294 13.5721 55.2518 13.2981C54.7742 13.0241 54.1753 12.8871 53.455 12.8871H49.9318V18.6871ZM67.2507 27.5554C68.4643 27.5554 69.5173 27.2815 70.4098 26.7335C71.3024 26.1855 71.9855 25.4292 72.4591 24.4644C72.9328 23.4997 73.1696 22.4076 73.1696 21.1882V20.9451C73.1696 19.7257 72.9328 18.6336 72.4591 17.6689C71.9855 16.7042 71.3004 15.9459 70.404 15.394C69.5075 14.8422 68.4486 14.5663 67.2273 14.5663C66.0216 14.5663 64.9724 14.8422 64.0799 15.394C63.1874 15.9459 62.5043 16.7042 62.0306 17.6689C61.557 18.6336 61.3201 19.7257 61.3201 20.9451V21.1882C61.3201 22.4076 61.557 23.4997 62.0306 24.4644C62.5043 25.4292 63.1893 26.1855 64.0858 26.7335C64.9822 27.2815 66.0372 27.5554 67.2507 27.5554ZM64.9959 24.1058C65.497 24.8622 66.2486 25.2404 67.2508 25.2404C67.9162 25.2404 68.4741 25.0609 68.9242 24.702C69.3744 24.3432 69.7072 23.8589 69.9225 23.2492C70.1378 22.6394 70.2454 21.9526 70.2454 21.1885V20.9454C70.2454 20.189 70.1378 19.5041 69.9225 18.8905C69.7072 18.2769 69.3725 17.7888 68.9184 17.426C68.4643 17.0633 67.9006 16.8819 67.2273 16.8819C66.5618 16.8819 66.0059 17.0633 65.5596 17.426C65.1134 17.7888 64.7826 18.2769 64.5673 18.8905C64.352 19.5041 64.2443 20.189 64.2443 20.9454V21.1885C64.2443 22.377 64.4949 23.3495 64.9959 24.1058ZM85.4179 31.4486C84.8189 32.0699 83.9636 32.3806 82.8518 32.3806C82.4682 32.3806 82.0024 32.3111 81.4543 32.1722V29.961L81.9945 29.9842C82.6913 29.9842 83.2218 29.8626 83.5858 29.6195C83.9499 29.3764 84.2376 28.95 84.449 28.3403L84.8483 27.2752L80.3856 14.7955H83.5447L86.1871 23.2002L88.7472 14.7955H91.8593L86.7978 29.2201C86.4768 30.0845 86.0168 30.8273 85.4179 31.4486ZM96.3776 27.3239V18.2709C96.8473 17.3679 97.642 16.9164 98.7616 16.9164C99.4975 16.9164 100.055 17.0939 100.435 17.4489C100.815 17.8039 101.005 18.4021 101.005 19.2433V27.3239H103.929V18.9771L103.917 18.7108C104.089 18.1628 104.381 17.7268 104.792 17.4026C105.203 17.0785 105.714 16.9164 106.325 16.9164C107.068 16.9164 107.626 17.0843 107.998 17.42C108.37 17.7557 108.556 18.3519 108.556 19.2086V27.3239H111.48V19.2202C111.48 17.6071 111.124 16.4282 110.411 15.6835C109.699 14.9387 108.669 14.5663 107.323 14.5663C106.469 14.5663 105.714 14.7342 105.056 15.0699C104.399 15.4056 103.854 15.8745 103.424 16.4765C103.103 15.8282 102.645 15.3477 102.05 15.0352C101.455 14.7226 100.758 14.5663 99.9594 14.5663C99.1765 14.5663 98.4778 14.6994 97.8632 14.9657C97.2486 15.232 96.726 15.6159 96.2954 16.1176L96.2014 14.7978H93.4534V27.3239H96.3776ZM120.167 27.1734C119.611 27.4281 118.981 27.5554 118.276 27.5554C117.446 27.5554 116.703 27.3934 116.045 27.0692C115.387 26.7451 114.874 26.2974 114.507 25.7263C114.139 25.1552 113.955 24.5146 113.955 23.8046C113.955 22.4694 114.458 21.4468 115.464 20.7367C116.47 20.0267 117.897 19.6717 119.744 19.6717H121.506V18.8613C121.506 18.1976 121.322 17.6785 120.954 17.3042C120.586 16.9299 120.042 16.7427 119.321 16.7427C118.891 16.7427 118.511 16.8161 118.182 16.9627C117.853 17.1093 117.599 17.31 117.419 17.5647C117.239 17.8194 117.149 18.1049 117.149 18.4214H114.225C114.225 17.7499 114.442 17.119 114.876 16.5286C115.311 15.9382 115.929 15.4635 116.732 15.1046C117.534 14.7457 118.452 14.5663 119.486 14.5663C120.457 14.5663 121.314 14.7284 122.058 15.0525C122.802 15.3767 123.383 15.8629 123.802 16.5112C124.221 17.1595 124.43 17.9506 124.43 18.8844V24.4066C124.43 25.5488 124.591 26.4556 124.912 27.1271V27.3239H121.929C121.803 27.0538 121.694 26.664 121.6 26.1547C121.2 26.5791 120.723 26.9187 120.167 27.1734ZM118.84 25.251C118.222 25.251 117.74 25.0908 117.396 24.7706C117.051 24.4503 116.879 24.0354 116.879 23.526C116.879 22.87 117.141 22.3607 117.666 21.9979C118.19 21.6352 118.962 21.4538 119.979 21.4538H121.506V23.7923C121.287 24.1936 120.94 24.5371 120.467 24.8226C119.993 25.1082 119.451 25.251 118.84 25.251ZM130.287 18.7918V27.3239H127.363V14.7978H130.146L130.228 16.2102C130.549 15.6854 130.949 15.2802 131.426 14.9946C131.904 14.7091 132.456 14.5663 133.082 14.5663C133.247 14.5663 133.432 14.5817 133.64 14.6126C133.847 14.6435 134.002 14.6782 134.104 14.7168L134.08 17.4373C133.689 17.3756 133.301 17.3447 132.918 17.3447C131.61 17.3447 130.733 17.8271 130.287 18.7918ZM138.786 27.3227V23.2592L140.067 21.9742L143.766 27.3227H147.148L141.946 20.0062L146.655 14.7966H143.132L139.656 18.6748L138.786 19.7399V9.54077H135.874V27.3227H138.786ZM157.057 26.884C156.235 27.3316 155.24 27.5554 154.074 27.5554C152.837 27.5554 151.756 27.2911 150.832 26.7624C149.909 26.2338 149.2 25.5102 148.707 24.5918C148.214 23.6734 147.967 22.643 147.967 21.5008V21.0261C147.967 19.7373 148.217 18.6047 148.719 17.6284C149.22 16.6521 149.909 15.8976 150.785 15.3651C151.662 14.8326 152.649 14.5663 153.745 14.5663C154.943 14.5663 155.949 14.8249 156.763 15.342C157.577 15.859 158.186 16.5826 158.589 17.5126C158.992 18.4426 159.194 19.5212 159.194 20.7483V21.9639H150.915C150.969 22.6044 151.138 23.1717 151.42 23.6656C151.701 24.1596 152.083 24.5455 152.565 24.8233C153.046 25.1012 153.608 25.2401 154.25 25.2401C155.565 25.2401 156.622 24.7307 157.421 23.712L158.983 25.2054C158.521 25.8768 157.879 26.4364 157.057 26.884ZM156.305 19.8803H150.973C151.106 18.931 151.408 18.194 151.878 17.6692C152.347 17.1443 152.966 16.8819 153.733 16.8819C154.594 16.8819 155.229 17.1424 155.636 17.6634C156.043 18.1843 156.266 18.85 156.305 19.6604V19.8803ZM167.317 27.2774C166.754 27.4626 166.12 27.5552 165.415 27.5552C164.381 27.5552 163.567 27.2735 162.972 26.7101C162.377 26.1467 162.08 25.2476 162.08 24.0127V16.9277H160.013V14.7976H162.08V11.7414H165.004V14.7976H167.235V16.9277H165.004V23.8159C165.004 24.3407 165.113 24.6977 165.333 24.8868C165.552 25.0759 165.881 25.1704 166.319 25.1704C166.632 25.1704 166.965 25.1318 167.317 25.0546V27.2774Z"
                                class="polymarket-logo-path"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M30.6525 17.4069V0L0 8.6333V8.77358V29.2266V29.3668L30.6525 38.0001V20.5933V17.4069ZM27.6857 16.5713V3.8835L5.16277 10.2277L27.6857 16.5713ZM25.4849 19.0001L2.96678 12.6572V25.3429L25.4849 19.0001ZM5.16283 27.7724L27.6857 34.1166V21.4289L5.16283 27.7724Z"
                                class="polymarket-logo-path"
                            />
                        </svg>
                    </a>

                    <div class="bottom-row-right-container">
                        <p
                            class="typography-secondary"
                        >
                            ${this.showVolume
                ? `$${numeral(volume).format("0.[0]a")}`
                : ""}
                            <span style="text-transform: capitalize;"
                                >${this.showVolume ? "Vol." : ""}</span
                            >
                        </p>
                        <a
                            href="${this._href}"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="button-primary"
                        >
                            Full Forecast
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="10"
                                viewBox="0 0 19 16"
                                class="arrow-icon"
                            >
                                <path
                                    d="M19 6.99997H4.414L9.707 1.70697L8.293 0.292969L0.585999 7.99997L8.293 15.707L9.707 14.293L4.414 8.99997H19V6.99997Z"
                                />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [EMBED_NAME]: ElectionEmbed;
    }
    namespace JSX {
        interface IntrinsicElements {
            [EMBED_NAME]: {
                event: string;
                volume?: boolean;
                theme?: "light" | "dark";
                creator?: string;
            };
        }
    }
}

/*
  Define the custom element if it hasn't been defined already.
  This is to prevent errors when using multiple versions of the polymarket-election-embed on the same page.
*/
if (!customElements.get(EMBED_NAME)) {
    customElements.define(EMBED_NAME, ElectionEmbed);
}
